<script lang="ts">
  import type { HTMLTextareaAttributes } from 'svelte/elements'
  import { vaultStore } from '$lib/stores/vaultStore'
  import { getVaultRelativePath, generateImageMarkdown } from '$lib/utils/imageUtils'
  import { getCaretCoordinates } from '$lib/utils/getCaretCoordinates'
  import { get } from 'svelte/store'

  interface Props extends HTMLTextareaAttributes {
    value: string
    placeholder?: string
    readonly?: boolean
    disabled?: boolean
    maxLength?: number
    minHeight?: number
    maxHeight?: number
    class?: string
  }

  let {
    value = $bindable(),
    placeholder = '',
    readonly = false,
    disabled = false,
    maxLength,
    minHeight,
    maxHeight,
    class: className = '',
    ...restProps
  }: Props = $props()

  // Check if parent handles scrolling
  const noScroll = className?.includes('editor-no-scroll') || className?.includes('no-scroll-wrapper')

  let textareaEl: HTMLTextAreaElement | null = null
  let wrapperEl: HTMLDivElement | null = null
  let isScrolling = $state(false)
  let scrollTimeout: ReturnType<typeof setTimeout> | null = null
  let lastDragOverTime = 0
  let dragOverThrottle = 50 // ms

  /**
   * BaseEditor: Common editor functionality
   * Single Responsibility: Basic text input handling
   */

  // Setup drag and drop handlers when textarea is mounted
  $effect(() => {
    if (textareaEl) {
      // Use capture phase to intercept events before textarea's default handling
      textareaEl.addEventListener('dragover', handleDragOver, true)
      textareaEl.addEventListener('drop', handleDrop, true)

      return () => {
        textareaEl?.removeEventListener('dragover', handleDragOver, true)
        textareaEl?.removeEventListener('drop', handleDrop, true)
      }
    }
  })
  
  function handleScroll() {
    isScrolling = true
    
    // Clear existing timeout
    if (scrollTimeout) {
      clearTimeout(scrollTimeout)
    }
    
    // Hide scrollbar after scrolling stops
    scrollTimeout = setTimeout(() => {
      isScrolling = false
    }, 2000) // Hide after 2 seconds of no scrolling
  }
  
  
  function getStyleString(): string {
    const styles: string[] = []
    if (minHeight) styles.push(`min-height: ${minHeight}px`)
    if (maxHeight) styles.push(`max-height: ${maxHeight}px`)
    return styles.join('; ')
  }
  
  export function focus() {
    textareaEl?.focus()
  }
  
  export function blur() {
    textareaEl?.blur()
  }
  
  export function select() {
    textareaEl?.select()
  }
  
  export function getElement(): HTMLTextAreaElement | null {
    return textareaEl
  }
  
  export function getSelection(): { text: string; start: number; end: number } | null {
    if (!textareaEl) return null

    const start = textareaEl.selectionStart
    const end = textareaEl.selectionEnd

    if (start === end) return null // No selection

    const text = value.substring(start, end)
    return { text, start, end }
  }

  /**
   * Handle drag over event for image drop
   */
  function handleDragOver(event: DragEvent) {
    // MUST preventDefault on ALL dragover to allow drop event to fire
    event.preventDefault()
    event.stopPropagation()

    // Check if this is an image from FileExplorer
    const types = event.dataTransfer?.types || []
    const hasImageData = types.some(type => type === 'application/x-image-file')

    if (hasImageData && event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy'

      // Throttle cursor position updates to avoid performance issues
      const now = Date.now()
      if (textareaEl && now - lastDragOverTime > dragOverThrottle) {
        lastDragOverTime = now

        // Show cursor at drop position during drag (simple approximation)
        const insertPos = getCharacterPositionFromPointFast(textareaEl, event.clientX, event.clientY)
        textareaEl.setSelectionRange(insertPos, insertPos)
      }
    }
  }

  /**
   * Fast approximation of character position from mouse coordinates
   * Used during dragover for performance
   */
  function getCharacterPositionFromPointFast(textarea: HTMLTextAreaElement, clientX: number, clientY: number): number {
    if (!textarea.value) return 0

    const rect = textarea.getBoundingClientRect()
    const style = window.getComputedStyle(textarea)

    // Get font metrics
    const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.8
    const fontSize = parseFloat(style.fontSize) || 16

    // Calculate relative position
    const relativeY = clientY - rect.top - parseFloat(style.paddingTop) + textarea.scrollTop
    const relativeX = clientX - rect.left - parseFloat(style.paddingLeft) + textarea.scrollLeft

    // Estimate line number
    const lineNumber = Math.floor(relativeY / lineHeight)
    const lines = textarea.value.split('\n')

    // Calculate offset to start of target line
    let offset = 0
    for (let i = 0; i < Math.min(lineNumber, lines.length); i++) {
      offset += lines[i].length + 1 // +1 for newline
    }

    // Estimate character within the line
    if (lineNumber >= 0 && lineNumber < lines.length) {
      const lineText = lines[lineNumber]
      const charWidth = fontSize * 0.6 // Approximation
      const charOffset = Math.floor(relativeX / charWidth)
      offset += Math.min(Math.max(0, charOffset), lineText.length)
    }

    return Math.max(0, Math.min(offset, textarea.value.length))
  }

  /**
   * Precise character position calculation for final drop
   * Uses getCaretCoordinates for accuracy
   */
  function getCharacterPositionFromPoint(textarea: HTMLTextAreaElement, clientX: number, clientY: number): number {
    if (!textarea.value) return 0

    const rect = textarea.getBoundingClientRect()
    const mouseY = clientY - rect.top + textarea.scrollTop
    const mouseX = clientX - rect.left + textarea.scrollLeft

    // Get approximate position first
    const approxPos = getCharacterPositionFromPointFast(textarea, clientX, clientY)

    // Refine around the approximate position (small range)
    const searchRadius = 20
    const start = Math.max(0, approxPos - searchRadius)
    const end = Math.min(textarea.value.length, approxPos + searchRadius)

    let bestPos = approxPos
    let minDistance = Infinity

    for (let pos = start; pos <= end; pos++) {
      const coords = getCaretCoordinates(textarea, pos)
      const dist = Math.sqrt(
        Math.pow(coords.top - mouseY, 2) +
        Math.pow(coords.left - mouseX, 2)
      )

      if (dist < minDistance) {
        minDistance = dist
        bestPos = pos
      }
    }

    return bestPos
  }

  /**
   * Handle drop event for image insertion
   */
  function handleDrop(event: DragEvent) {
    // Get image file data from FileExplorer
    const imageData = event.dataTransfer?.getData('application/x-image-file')

    if (!imageData) {
      return
    }

    event.preventDefault()
    // Don't stopPropagation - allow dragend to reach FileExplorer

    try {
      const { path } = JSON.parse(imageData)

      // Get vault path for relative path calculation
      const vault = get(vaultStore)
      const vaultPath = vault?.vaultPath || ''

      // Convert to vault-relative path
      const relativePath = getVaultRelativePath(path, vaultPath)

      // Generate markdown image syntax
      const imageMarkdown = generateImageMarkdown(relativePath)

      // Insert at drop position
      if (textareaEl) {
        // Calculate precise insertion position at drop time
        const insertPos = getCharacterPositionFromPoint(textareaEl, event.clientX, event.clientY)

        const before = value.substring(0, insertPos)
        const after = value.substring(insertPos)

        // Add newlines around image for proper markdown formatting
        const needsNewlineBefore = before.length > 0 && !before.endsWith('\n')
        const needsNewlineAfter = after.length > 0 && !after.startsWith('\n')

        const prefix = needsNewlineBefore ? '\n' : ''
        const suffix = needsNewlineAfter ? '\n' : ''

        const newValue = before + prefix + imageMarkdown + suffix + after

        // Update both the reactive value AND the DOM directly
        value = newValue
        textareaEl.value = newValue

        // Move cursor after the inserted image
        const newCursorPos = insertPos + prefix.length + imageMarkdown.length + suffix.length

        // Set cursor position
        textareaEl.focus()
        textareaEl.setSelectionRange(newCursorPos, newCursorPos)

        // Trigger input event to notify parent components
        const inputEvent = new Event('input', { bubbles: true })
        textareaEl.dispatchEvent(inputEvent)
      }
    } catch (error) {
      console.error('[BaseEditor] Failed to insert image:', error)
    }
  }
</script>

<div
  class="base-editor-wrapper {noScroll ? 'no-scroll' : ''}"
  bind:this={wrapperEl}
>
  <textarea
    bind:this={textareaEl}
    bind:value
    class="base-editor {className}"
    {placeholder}
    {readonly}
    {disabled}
    {maxLength}
    style={getStyleString()}
    spellcheck="false"
    autocomplete="off"
    {...restProps}
  ></textarea>
</div>

<style>
  .base-editor-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
  }
  
  /* When parent handles scrolling, disable wrapper scrolling */
  .base-editor-wrapper.no-scroll {
    overflow: visible !important;
    overflow-y: visible !important;
    overflow-x: visible !important;
    height: 100%; /* Fill parent height exactly */
  }
  
  .base-editor {
    width: 100%;
    min-height: 100%;
    padding: 20px max(32px, calc(50% - 500px));
    border: none;
    background: transparent;
    box-sizing: border-box;
    font-family: var(--editor-font-family, inherit);
    font-size: var(--editor-font-size, 16px);
    line-height: var(--editor-line-height, 1.8);
    color: var(--text-primary);
    resize: none;
    outline: none;
    overflow: visible; /* Never let textarea scroll on its own */
    contain: layout style;
    backface-visibility: hidden;
    transform: translateZ(0); /* GPU acceleration */
    will-change: auto; /* Let browser optimize */
    /* Ensure consistent text rendering */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeSpeed; /* Optimize for speed during typing */
    font-kerning: normal;
    font-feature-settings: normal;
    font-variant-ligatures: none; /* Disable ligatures for exact matching */
    /* Improve IME input performance */
    ime-mode: active;
    -webkit-ime-mode: active;
  }
  
  .base-editor:focus {
    outline: none;
  }
  
  .base-editor::placeholder {
    color: var(--text-tertiary);
  }
  
  .base-editor:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 1064px) {
    .base-editor {
      padding: 20px 32px;
    }
  }

  @media (max-width: 600px) {
    .base-editor {
      padding: 20px 16px;
    }
  }
</style>